import { Schema } from '@tunasong/icons'
import type { SvgIconComponent } from '@tunasong/icons'
import { logger } from '@tunasong/models'
import type { Command, DefaultGlobalContext, GenerateEntityCommand } from '@tunasong/plugin-lib'
import { profilesApi } from '@tunasong/redux'
import type { BandSpace, Persisted, Rider } from '@tunasong/schemas'
import { isBandSpace, isPersisted, isRider } from '@tunasong/schemas'
import { syncRiderFromProfiles } from '../sync.js'

/**
 * Get member user IDs from entity ACLs and owner
 */
function getMemberIds(entity: Persisted<BandSpace>): string[] {
  const ids = new Set<string>()
  ids.add(entity.userId)
  for (const acl of entity.acls ?? []) {
    if (typeof acl.principal === 'string' && !['PUBLIC', 'AUTHENTICATED'].includes(acl.principal)) {
      ids.add(acl.principal)
    }
  }
  return Array.from(ids)
}

export const syncRiderCommand = ({
  entity,
  globalContext: { graph, dispatch },
}: GenerateEntityCommand<DefaultGlobalContext>): Command[] => {
  if (!entity || !isRider(entity) || !isPersisted(entity)) {
    return []
  }

  const rider = entity as Persisted<Rider>

  // Only show sync command if rider has a parent (bandspace)
  if (!rider.parentId) {
    return []
  }

  return [
    {
      id: 'rider.sync',
      name: 'Sync from Profiles',
      Icon: Schema as SvgIconComponent,
      color: 'deepOrange',

      cmd: async () => {
        // Get parent bandspace
        if (!rider.parentId) {
          logger.warn('Rider has no parent', { riderId: rider.id })
          return
        }

        const parent = await graph.getEntity(rider.parentId)
        if (!parent || !isBandSpace(parent) || !isPersisted(parent)) {
          logger.warn('Rider parent is not a bandspace', { riderId: rider.id, parentId: rider.parentId })
          return
        }

        const bandspace = parent as Persisted<BandSpace>

        // Fetch member profiles
        const memberIds = getMemberIds(bandspace)
        logger.info('Syncing rider from member profiles', { riderId: rider.id, memberIds })

        const result = await dispatch(profilesApi.endpoints.loadProfiles.initiate({ userIds: memberIds }))

        if (result.error || !result.data?.length) {
          logger.warn('Failed to load profiles for sync, skipping', { riderId: rider.id, error: result.error })
          return
        }

        const profiles = result.data

        logger.info('Fetched profiles for sync', {
          riderId: rider.id,
          profileCount: profiles.length,
          profiles: profiles.map(p => ({ userId: p.userId, hasConfig: !!p.config, config: p.config })),
        })

        // Generate updates
        const updates = syncRiderFromProfiles(rider, bandspace, profiles)

        logger.info('Generated rider updates', {
          riderId: rider.id,
          memberCount: updates.members?.length,
          members: updates.members?.map(m => ({
            userId: m.userId,
            requirementCount: m.requirements.length,
            requirements: m.requirements,
          })),
        })

        // Update the rider
        await graph.updateEntity({
          id: rider.id,
          partialEntity: updates,
          eTag: null,
        })

        logger.info('Synced rider with profiles', { riderId: rider.id })
      },
    },
  ]
}
